import {
  App,
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { FilePdfOutlined, FileProtectOutlined, PlusOutlined, RetweetOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { frontDeskApi, type Issuance } from '@/api/frontdesk';
import { studentsApi } from '@/api/students';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';
import { downloadFile } from '@/shared/lib/download';

interface IssueShape {
  documentTypeId: number;
  studentId: number;
  issueDate: Dayjs;
}

interface ReissueShape {
  issueDate: Dayjs;
  reason: string;
}

export function IssuancesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FRONT_DESK:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FRONT_DESK:EDIT'));

  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(60, 'day'), dayjs()]);
  const [studentSearch, setStudentSearch] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [reissuing, setReissuing] = useState<Issuance | null>(null);
  const [issueForm] = Form.useForm<IssueShape>();
  const [reissueForm] = Form.useForm<ReissueShape>();

  const params = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
  const { data, isFetching } = useQuery({
    queryKey: ['frontdesk', 'issuances', params],
    queryFn: () => frontDeskApi.issuancesInRange(params.from, params.to),
  });

  const { data: types } = useQuery({
    queryKey: ['frontdesk', 'document-types', 'active'],
    queryFn: () => frontDeskApi.listDocumentTypes(true),
    staleTime: 5 * 60_000,
  });

  const { data: studentResults } = useQuery({
    queryKey: ['students', 'search', studentSearch],
    queryFn: () => studentsApi.search({ q: studentSearch.trim() || undefined, size: 15 }),
    enabled: studentSearch.trim().length > 0,
  });

  const issue = useMutation({
    mutationFn: (v: IssueShape) =>
      frontDeskApi.issueDocument({
        documentTypeId: v.documentTypeId,
        studentId: v.studentId,
        issueDate: v.issueDate.format('YYYY-MM-DD'),
      }),
    onSuccess: (i) => {
      message.success(`Issued ${i.documentRef}`);
      qc.invalidateQueries({ queryKey: ['frontdesk', 'issuances'] });
      qc.invalidateQueries({ queryKey: ['frontdesk', 'document-types'] });
      setIssuing(false);
      issueForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reissue = useMutation({
    mutationFn: (v: ReissueShape) =>
      frontDeskApi.reissueDocument({
        originalIssuanceId: reissuing!.id,
        issueDate: v.issueDate.format('YYYY-MM-DD'),
        reason: v.reason.trim(),
      }),
    onSuccess: (i) => {
      message.success(`Reissued as ${i.documentRef}`);
      qc.invalidateQueries({ queryKey: ['frontdesk', 'issuances'] });
      qc.invalidateQueries({ queryKey: ['frontdesk', 'document-types'] });
      setReissuing(null);
      reissueForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openIssue = () => {
    issueForm.resetFields();
    issueForm.setFieldsValue({ issueDate: dayjs() });
    setIssuing(true);
  };
  const openReissue = (row: Issuance) => {
    reissueForm.resetFields();
    reissueForm.setFieldsValue({ issueDate: dayjs() });
    setReissuing(row);
  };

  const studentOptions =
    studentResults?.content.map((s) => ({
      value: s.id,
      label: `${s.firstName} ${s.lastName} · ${s.registrationNumber}`,
    })) ?? [];

  const columns: TableProps<Issuance>['columns'] = [
    {
      title: 'Ref',
      dataIndex: 'documentRef',
      width: 120,
      render: (r: string) => <Tag color="processing" style={{ fontFamily: 'monospace' }}>{r}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: 'documentTypeName',
      width: 200,
    },
    {
      title: 'Student',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.studentName}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.studentRegistrationNumber}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Issued',
      dataIndex: 'issueDate',
      width: 130,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Reissue',
      width: 220,
      render: (_, row) =>
        row.reissue ? (
          <Space orientation="vertical" size={0}>
            <Tag color="warning" icon={<RetweetOutlined />}>
              Reissue of #{row.originalDocumentNumber}
            </Tag>
            {row.reissueReason && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {row.reissueReason}
              </Typography.Text>
            )}
          </Space>
        ) : (
          <span style={{ opacity: 0.4 }}>—</span>
        ),
    },
    {
      title: 'Actions',
      width: 200,
      align: 'right',
      render: (_, row) => (
        <Space size={0}>
          <Button
            size="small"
            type="text"
            icon={<FilePdfOutlined />}
            onClick={() =>
              downloadFile(`/reports/exports/document-issuance/${row.id}.pdf`, {},
                `${row.documentTypeCode ?? 'document'}-${row.documentNumber}.pdf`)
            }
          >
            PDF
          </Button>
          {canEdit && (
            <Button size="small" type="text" icon={<RetweetOutlined />} onClick={() => openReissue(row)}>
              Reissue
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Front Desk"
        title="Document issuance"
        description="Sequential numbering per document type. Reissues link back to the original."
        count={data ? { value: data.length, label: 'issued' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openIssue}>
              Issue document
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No documents issued in this range" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isFetching}
          columns={columns}
          dataSource={data ?? []}
          pagination={{ pageSize: 25 }}
        />
      )}

      <Modal
        title="Issue document"
        open={issuing}
        onCancel={() => setIssuing(false)}
        onOk={() => issueForm.submit()}
        confirmLoading={issue.isPending}
        okText="Issue"
        okButtonProps={{ icon: <FileProtectOutlined /> }}
        destroyOnHidden
      >
        {issuing && (
          <Form form={issueForm} layout="vertical" onFinish={(v) => issue.mutate(v)}>
            <Form.Item label="Document type" name="documentTypeId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Pick a type"
                options={(types ?? []).map((t) => ({
                  value: t.id,
                  label: `${t.name} (${t.code})`,
                }))}
              />
            </Form.Item>
            <Form.Item label="Student" name="studentId" rules={[{ required: true }]}>
              <Select
                showSearch
                placeholder="Search by name or registration number"
                filterOption={false}
                onSearch={(v) => setStudentSearch(v)}
                notFoundContent={studentSearch.trim() === '' ? 'Type to search…' : 'No matches'}
                options={studentOptions}
              />
            </Form.Item>
            <Form.Item label="Issue date" name="issueDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
              The next sequential number for this type will be assigned automatically.
            </Typography.Paragraph>
          </Form>
        )}
      </Modal>

      {reissuing && (
        <Modal
          open
          title={`Reissue · ${reissuing.documentRef} for ${reissuing.studentName}`}
          onCancel={() => setReissuing(null)}
          onOk={() => reissueForm.submit()}
          confirmLoading={reissue.isPending}
          okText="Reissue"
          okButtonProps={{ icon: <RetweetOutlined /> }}
          destroyOnHidden
        >
          <Form form={reissueForm} layout="vertical" onFinish={(v) => reissue.mutate(v)}>
            <Form.Item label="Issue date" name="issueDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Form.Item label="Reason" name="reason" rules={[{ required: true, max: 2000 }]}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="e.g. Original document lost" />
            </Form.Item>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
              A new sequential number will be assigned. The link to the original is preserved.
            </Typography.Paragraph>
          </Form>
        </Modal>
      )}
    </div>
  );
}
